import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router";

import { Alert, Button, Card, TextField } from "../../components/ui";
import { api, applyFieldErrors, errorMessage } from "../../lib/api";
import type { Message } from "../../lib/types";
import { PASSWORD_MIN, registerSchema, type RegisterValues } from "./schemas";

export function RegisterPage() {
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    setError: setFieldError,
    formState: { errors, isSubmitting },
  } = useForm<RegisterValues>({ resolver: zodResolver(registerSchema) });

  const submit = async ({ confirm_password: _c, ...values }: RegisterValues) => {
    setError(null);
    try {
      const res = await api.post<Message>("/auth/register", {
        ...values,
        phone: values.phone || null,
      });
      setDone(res.message);
    } catch (err) {
      if (!applyFieldErrors(err, setFieldError)) setError(errorMessage(err));
    }
  };

  if (done)
    return (
      <div className="mx-auto max-w-md">
        <h1 className="mb-4 text-2xl font-bold">Check your email</h1>
        <Alert tone="success">{done}</Alert>
        <p className="mt-4 text-sm">
          Already confirmed?{" "}
          <Link to="/login" className="text-brand-700 underline">
            Sign in
          </Link>
        </p>
      </div>
    );

  return (
    <div className="mx-auto max-w-lg">
      <h1 className="mb-2 text-2xl font-bold">Create your account</h1>
      <p className="mb-4 text-ink-muted">
        Each person gets their own sign-in. If you work with a company that already uses the portal,
        you can ask to join it after you sign in.
      </p>
      <Card>
        <form noValidate onSubmit={handleSubmit(submit)} className="grid gap-4 sm:grid-cols-2">
          {error && (
            <div className="sm:col-span-2">
              <Alert tone="error">{error}</Alert>
            </div>
          )}
          <TextField
            label="First name"
            autoComplete="given-name"
            required
            error={errors.first_name?.message}
            {...register("first_name")}
          />
          <TextField
            label="Last name"
            autoComplete="family-name"
            required
            error={errors.last_name?.message}
            {...register("last_name")}
          />
          <div className="sm:col-span-2">
            <TextField
              label="Email"
              type="email"
              autoComplete="email"
              required
              error={errors.email?.message}
              {...register("email")}
            />
          </div>
          <div className="sm:col-span-2">
            <TextField
              label="Phone"
              type="tel"
              autoComplete="tel"
              error={errors.phone?.message}
              {...register("phone")}
            />
          </div>
          <TextField
            label="Password"
            type="password"
            autoComplete="new-password"
            required
            hint={`At least ${PASSWORD_MIN} characters.`}
            error={errors.password?.message}
            {...register("password")}
          />
          <TextField
            label="Repeat password"
            type="password"
            autoComplete="new-password"
            required
            error={errors.confirm_password?.message}
            {...register("confirm_password")}
          />
          <div className="sm:col-span-2">
            <Button type="submit" busy={isSubmitting} className="w-full">
              Create account
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
